import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";

import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import type { TeamMemberDto } from "~/lib/api";
import { discardPatientDraft, fetchPatientDraft, savePatientDraft } from "~/lib/patient-drafts";
import {
  createPatient,
  fetchCurrentUserId,
  fetchPatient,
  fetchSessionTeam,
  toFormValues,
  updatePatient,
} from "~/lib/patients";
import { isOutgoingDoctor, patientEditLockReason } from "~/lib/permissions";
import NotFoundRoute from "~/routes/not-found";
import { useSession } from "~/session/session-context";

import { PatientForm } from "./patient-form";
import { EMPTY_PATIENT_FORM, type PatientFormValues } from "./schema";

type ScreenState =
  | { kind: "loading" }
  | { kind: "not-found" }
  | { kind: "error"; message: string }
  | {
      kind: "ready";
      defaultValues: PatientFormValues;
      teamMembers: TeamMemberDto[];
      readOnly: boolean;
      lockedReason: string | null;
    };

const SESSION_LOCKED_REASON = "This handover is locked. Patients can no longer be added or edited.";
const ACKNOWLEDGED_REASON = "This patient has been acknowledged and can no longer be edited.";

/**
 * The Add/Edit patient screen (E4.1), rendered inside `<SessionLayout>` (so the
 * session — and the caller's ward access — is already resolved). It loads the
 * ward team, the patient (edit mode), and the caller's id, then resolves the
 * access rules from the ticket:
 *
 *   • not the session's outgoing doctor  → 404 (never reveal the form exists)
 *   • locked session / acknowledged patient → read-only with a 423 banner
 *   • otherwise → the editable form
 *
 * The server enforces the same rules; this mirror just avoids showing a form the
 * caller can't submit.
 */
export function PatientFormScreen({ mode }: { mode: "create" | "edit" }): JSX.Element {
  const { session } = useSession();
  const { id: sessionId, patientId } = useParams();
  const navigate = useNavigate();

  const [state, setState] = useState<ScreenState>({ kind: "loading" });
  const [reloadKey, setReloadKey] = useState(0);
  const [serverError, setServerError] = useState<string | null>(null);

  const load = useCallback(
    async (signal: { cancelled: boolean }): Promise<void> => {
      if (!sessionId || (mode === "edit" && !patientId)) {
        setState({ kind: "not-found" });
        return;
      }
      setState({ kind: "loading" });

      const [currentUserId, teamResult, patientResult] = await Promise.all([
        fetchCurrentUserId(),
        fetchSessionTeam(sessionId),
        mode === "edit" && patientId ? fetchPatient(sessionId, patientId) : Promise.resolve(null),
      ]);
      if (signal.cancelled) return;

      if (teamResult.status === "not-found" || patientResult?.status === "not-found") {
        setState({ kind: "not-found" });
        return;
      }
      if (teamResult.status === "error") {
        setState({ kind: "error", message: teamResult.message });
        return;
      }
      if (patientResult?.status === "error") {
        setState({ kind: "error", message: patientResult.message });
        return;
      }

      // Only the outgoing doctor may add/edit. Anyone else is shown a 404 rather
      // than a forbidden form, matching the API's "don't reveal" posture.
      if (!isOutgoingDoctor(currentUserId ? { id: currentUserId } : null, session)) {
        setState({ kind: "not-found" });
        return;
      }

      const patient = patientResult?.status === "ok" ? patientResult.data : null;
      const lockReason = patientEditLockReason(session, patient);
      const lockedReason =
        lockReason === "session-locked"
          ? SESSION_LOCKED_REASON
          : lockReason === "acknowledged"
            ? ACKNOWLEDGED_REASON
            : null;
      const readOnly = lockedReason != null;

      const base = patient ? toFormValues(patient) : EMPTY_PATIENT_FORM;
      // Restore an auto-saved draft if one exists and the form is still editable
      // (HAN-72). A draft is unvalidated scratch work, so it overrides the saved
      // patient / empty defaults wholesale. Locked/acknowledged forms don't
      // restore — there's nothing to edit.
      const draft = readOnly
        ? null
        : await fetchPatientDraft(sessionId, mode === "edit" ? patientId : undefined);
      if (signal.cancelled) return;

      setState({
        kind: "ready",
        defaultValues: draft ?? base,
        teamMembers: teamResult.data,
        readOnly,
        lockedReason,
      });
    },
    [mode, patientId, session, sessionId],
  );

  useEffect(() => {
    const signal = { cancelled: false };
    void load(signal);
    return () => {
      signal.cancelled = true;
    };
  }, [load, reloadKey]);

  const backToList = useCallback(() => {
    navigate(`/session/${sessionId}`);
  }, [navigate, sessionId]);

  // Auto-save the in-progress form as a draft (HAN-72). Best-effort: the boolean
  // drives only the "draft saved" indicator. The create/edit drafts are keyed by
  // the patientId (undefined for create).
  const autoSaveDraft = useCallback(
    async (values: PatientFormValues): Promise<boolean> => {
      if (!sessionId) return false;
      return savePatientDraft(sessionId, mode === "edit" ? patientId : undefined, values);
    },
    [mode, patientId, sessionId],
  );

  const handleSubmit = useCallback(
    async (values: PatientFormValues): Promise<void> => {
      if (!sessionId) return;
      setServerError(null);
      const result =
        mode === "edit" && patientId
          ? await updatePatient(sessionId, patientId, values)
          : await createPatient(sessionId, values);
      if (result.status === "ok") {
        // Drop the draft so it never re-restores over the saved patient.
        void discardPatientDraft(sessionId, mode === "edit" ? patientId : undefined);
        backToList();
        return;
      }
      setServerError(result.message);
    },
    [backToList, mode, patientId, sessionId],
  );

  if (state.kind === "loading") {
    return (
      <div className="flex items-center justify-center py-16" aria-live="polite">
        <Loader2
          className="h-6 w-6 animate-spin text-muted-foreground"
          aria-label="Loading patient form"
        />
      </div>
    );
  }

  if (state.kind === "not-found") {
    return <NotFoundRoute />;
  }

  if (state.kind === "error") {
    return (
      <Alert variant="destructive">
        <AlertTitle>Something went wrong</AlertTitle>
        <AlertDescription className="flex flex-col items-start gap-3">
          <span>{state.message}</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setReloadKey((k) => k + 1);
            }}
          >
            Try again
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  // A locked create has nothing to show but the notice; a locked edit shows the
  // patient read-only beneath the banner.
  const showForm = mode === "edit" || !state.readOnly;

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <h1 className="text-xl font-semibold tracking-tight">
        {mode === "create" ? "Add patient" : "Edit patient"}
      </h1>

      {state.lockedReason ? (
        <Alert variant="destructive" role="status">
          <AlertTitle>Locked</AlertTitle>
          <AlertDescription>{state.lockedReason}</AlertDescription>
        </Alert>
      ) : null}

      {showForm ? (
        <PatientForm
          mode={mode}
          defaultValues={state.defaultValues}
          teamMembers={state.teamMembers}
          onSubmit={handleSubmit}
          onCancel={backToList}
          readOnly={state.readOnly}
          serverError={serverError}
          autoSaveDraft={state.readOnly ? undefined : autoSaveDraft}
        />
      ) : (
        <Button type="button" variant="outline" onClick={backToList}>
          Back
        </Button>
      )}
    </div>
  );
}
